/**
 * ControlsBar — Top controls for the Vehicle Scene Timeline
 *
 * Vehicle selector, time window, asOf, view mode, lane toggles,
 * and confidence threshold slider.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Truck, Clock, Eye, EyeOff, Layers, SlidersHorizontal,
  RefreshCw, Calendar, Crosshair, ChevronDown, Search,
} from 'lucide-react';
import type { TimelineControls, ViewMode, LaneVisibility } from '../../types/vehicleSceneTimeline';
import type { VehicleListItem } from '../../services/truthLayerApi';

interface ControlsBarProps {
  controls: TimelineControls;
  onChange: React.Dispatch<React.SetStateAction<TimelineControls>>;
  onRefresh: () => void;
  loading: boolean;
  asOfLabel?: string | null;
  vehicleList: VehicleListItem[];
  vehicleListLoading?: boolean;
}

const LANE_LABELS: { key: keyof LaneVisibility; label: string; color: string }[] = [
  { key: 'movement', label: 'Movement', color: '#22c55e' },
  { key: 'orders', label: 'Orders (graph)', color: '#3b82f6' },
  { key: 'drivers', label: 'Drivers (graph)', color: '#8b5cf6' },
  { key: 'fuel', label: 'Fuel (graph)', color: '#f59e0b' },
  { key: 'attribution', label: 'Attribution', color: '#ec4899' },
];

export default function ControlsBar({
  controls,
  onChange,
  onRefresh,
  loading,
  asOfLabel,
  vehicleList,
  vehicleListLoading,
}: ControlsBarProps) {
  const [asOfEnabled, setAsOfEnabled] = useState(!!controls.asOf);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const vehicleComboRef = useRef<HTMLDivElement>(null);

  const sortedVehicleList = useMemo(
    () => [...vehicleList].sort((a, b) => a.label.localeCompare(b.label)),
    [vehicleList],
  );

  const filteredVehicleList = useMemo(
    () =>
      vehicleSearch
        ? sortedVehicleList.filter(v =>
            v.label.toLowerCase().includes(vehicleSearch.toLowerCase()),
          )
        : sortedVehicleList,
    [sortedVehicleList, vehicleSearch],
  );

  const selectedVehicle = sortedVehicleList.find(v => v.id === controls.vehicleId);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (vehicleComboRef.current && !vehicleComboRef.current.contains(e.target as Node)) {
        setVehicleDropdownOpen(false);
        setVehicleSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateField = <K extends keyof TimelineControls>(
    key: K,
    value: TimelineControls[K],
  ) => {
    onChange(prev => ({ ...prev, [key]: value }));
  };

  const toggleLane = (lane: keyof LaneVisibility) => {
    onChange(prev => ({
      ...prev,
      lanes: { ...prev.lanes, [lane]: !prev.lanes[lane] },
    }));
  };

  const handleAsOfToggle = () => {
    const next = !asOfEnabled;
    setAsOfEnabled(next);
    updateField('asOf', next ? new Date().toISOString() : null);
  };

  return (
    <div className="vst-controls-bar">
      {/* Row 1: Vehicle + Time window + asOf + Refresh */}
      <div className="vst-controls-row">
        {/* Vehicle selector */}
        <div className="vst-control-group">
          <label className="vst-label">
            <Truck size={14} />
            Vehicle
          </label>
          <div className="vst-vehicle-combo" ref={vehicleComboRef}>
            {vehicleDropdownOpen ? (
              <div className="vst-vehicle-search-wrap">
                <Search size={11} className="vst-vehicle-search-icon" />
                <input
                  autoFocus
                  className="vst-vehicle-search"
                  placeholder="Search…"
                  value={vehicleSearch}
                  onChange={e => setVehicleSearch(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setVehicleDropdownOpen(false);
                      setVehicleSearch('');
                    } else if (e.key === 'Enter' && filteredVehicleList.length > 0) {
                      updateField('vehicleId', filteredVehicleList[0].id);
                      setVehicleDropdownOpen(false);
                      setVehicleSearch('');
                    }
                  }}
                />
              </div>
            ) : (
              <button
                className="vst-vehicle-trigger"
                onClick={() => !vehicleListLoading && setVehicleDropdownOpen(true)}
                disabled={vehicleListLoading}
              >
                <span className="vst-vehicle-trigger-label">
                  {vehicleListLoading ? 'Loading…' : (selectedVehicle?.label ?? controls.vehicleId)}
                </span>
                <ChevronDown size={11} />
              </button>
            )}
            {vehicleDropdownOpen && (
              <div className="vst-vehicle-dropdown">
                {filteredVehicleList.length === 0 ? (
                  <div className="vst-vehicle-no-results">No matches</div>
                ) : (
                  filteredVehicleList.map(v => (
                    <div
                      key={v.id}
                      className={`vst-vehicle-option${v.id === controls.vehicleId ? ' selected' : ''}`}
                      onMouseDown={e => {
                        e.preventDefault();
                        updateField('vehicleId', v.id);
                        setVehicleDropdownOpen(false);
                        setVehicleSearch('');
                      }}
                    >
                      {v.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Time window */}
        <div className="vst-control-group">
          <label className="vst-label">
            <Clock size={14} />
            From
          </label>
          <input
            type="datetime-local"
            className="vst-input"
            value={controls.from.slice(0, 16)}
            onChange={e =>
              updateField('from', new Date(e.target.value).toISOString())
            }
          />
        </div>
        <div className="vst-control-group">
          <label className="vst-label">To</label>
          <input
            type="datetime-local"
            className="vst-input"
            value={controls.to.slice(0, 16)}
            onChange={e =>
              updateField('to', new Date(e.target.value).toISOString())
            }
          />
        </div>

        {/* asOf toggle */}
        <div className="vst-control-group">
          <label className="vst-label">
            <Calendar size={14} />
            asOf
          </label>
          <div className="vst-asof-group">
            <button
              className={`vst-toggle-btn ${asOfEnabled ? 'active' : ''}`}
              onClick={handleAsOfToggle}
              title="Enable as-of time travel"
            >
              {asOfEnabled ? 'ON' : 'OFF'}
            </button>
            {asOfEnabled && (
              <input
                type="datetime-local"
                className="vst-input vst-input-sm"
                value={(controls.asOf || '').slice(0, 16)}
                onChange={e =>
                  updateField('asOf', new Date(e.target.value).toISOString())
                }
              />
            )}
          </div>
          {asOfLabel && (
            <span className="vst-asof-badge">as of {asOfLabel}</span>
          )}
        </div>

        {/* Refresh */}
        <button
          className="vst-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
          title="Reload timeline data"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Row 2: View mode + Lane toggles + Confidence threshold */}
      <div className="vst-controls-row">
        {/* View mode */}
        <div className="vst-control-group">
          <label className="vst-label">
            <Layers size={14} />
            View
          </label>
          <div className="vst-btn-group">
            {(['STACKED', 'OVERLAY'] as ViewMode[]).map(mode => (
              <button
                key={mode}
                className={`vst-mode-btn ${controls.viewMode === mode ? 'active' : ''}`}
                onClick={() => updateField('viewMode', mode)}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {/* Lane toggles */}
        <div className="vst-control-group">
          <label className="vst-label">Lanes</label>
          <div className="vst-lane-toggles">
            {LANE_LABELS.map(({ key, label, color }) => (
              <button
                key={key}
                className={`vst-lane-toggle ${controls.lanes[key] ? 'active' : ''}`}
                onClick={() => toggleLane(key)}
                style={{ '--lane-color': color } as React.CSSProperties}
              >
                {controls.lanes[key] ? (
                  <Eye size={12} />
                ) : (
                  <EyeOff size={12} />
                )}
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Confidence threshold */}
        <div className="vst-control-group">
          <label className="vst-label">
            <SlidersHorizontal size={14} />
            Confidence ≥ {Math.round(controls.confidenceThreshold * 100)}%
          </label>
          <input
            type="range"
            className="vst-slider"
            min={0}
            max={100}
            step={5}
            value={Math.round(controls.confidenceThreshold * 100)}
            onChange={e =>
              updateField('confidenceThreshold', parseInt(e.target.value) / 100)
            }
          />
        </div>

        {/* Playhead toggle */}
        <div className="vst-control-group">
          <label className="vst-label">
            <Crosshair size={14} />
            Playhead
          </label>
          <button
            className={`vst-toggle-btn ${controls.playheadEnabled ? 'active' : ''}`}
            onClick={() => updateField('playheadEnabled', !controls.playheadEnabled)}
            title={controls.playheadEnabled ? 'Disable playhead' : 'Enable playhead'}
          >
            {controls.playheadEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>
    </div>
  );
}
